import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Truck, ArrowRight, ShieldCheck, Clock, Snowflake, ChefHat, Info, CheckCircle2, Play } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Tooltip } from '../ui/Tooltip';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
const steps = [{
  id: 1,
  icon: ClipboardCheck,
  title: 'El Diagnóstico',
  subtitle: 'Ciencia de Datos',
  description: 'Analizamos 15 puntos de datos de tu mascota (raza, edad, actividad) para calcular sus necesidades calóricas exactas.',
  detail: 'Algoritmo Veterinario',
  color: 'text-blue-600',
  bg: 'bg-blue-50',
  border: 'border-blue-100',
  time: 'Día 1'
}, {
  id: 2,
  icon: ChefHat,
  title: 'La Cocina',
  subtitle: 'Método Sous-Vide',
  description: 'Cocinamos al vacío a baja temperatura (80°C) para eliminar patógenos preservando todas las vitaminas y el sabor.',
  detail: 'Certificado IFS Food',
  color: 'text-amber-600',
  bg: 'bg-amber-50',
  border: 'border-amber-100',
  time: 'Día 2'
}, {
  id: 3,
  icon: Snowflake,
  title: 'La Conservación',
  subtitle: 'Ultra-Congelación',
  description: 'Abatimos la temperatura a -30°C instantáneamente. Sin conservantes químicos, solo el poder natural del frío.',
  detail: 'Seguridad ISO 22000',
  color: 'text-cyan-600',
  bg: 'bg-cyan-50',
  border: 'border-cyan-100',
  time: 'Día 2'
}, {
  id: 4,
  icon: Truck,
  title: 'La Entrega',
  subtitle: 'Cadena de Frío',
  description: 'Recibes las raciones exactas en transporte refrigerado. Descongela, sirve y observa la transformación.',
  detail: 'Entrega 24/48h',
  color: 'text-emerald-600',
  bg: 'bg-emerald-50',
  border: 'border-emerald-100',
  time: 'Día 3'
}];
export function HowItWorks() {
  return <section className="py-24 lg:py-32 bg-[#FAF7F2] relative overflow-hidden" data-id="element-2113">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(#2D4A3E_1px,transparent_1px)] [background-size:24px_24px]" data-id="element-2114" />

      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-2115">
        <div className="text-center mb-20 max-w-3xl mx-auto" data-id="element-2116">
          <EnhancedScrollReveal data-id="element-2117">
            <Badge variant="secondary" className="mb-6" data-id="element-2118">
              Transparencia Radical
            </Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-charcoal-900 mb-6" data-id="element-2119">
              Del laboratorio a tu cuenco
            </h2>
            <p className="text-lg md:text-xl text-charcoal-600 font-light leading-relaxed" data-id="element-2120">
              Un proceso diseñado obsesivamente para mantener la integridad
              nutricional de cada ingrediente. Sin atajos industriales.
            </p>
          </EnhancedScrollReveal>
        </div>

        {/* Visual Journey */}
        <div className="relative mb-24" data-id="element-2121">
          {/* Connecting Line (Desktop) */}
          <div className="hidden lg:block absolute top-24 left-0 w-full h-0.5 bg-gray-200 -z-10" data-id="element-2122">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '100%'
          }} transition={{
            duration: 2,
            ease: 'linear'
          }} className="h-full bg-gradient-to-r from-blue-400 via-amber-400 to-emerald-400" data-id="element-2123" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12" data-id="element-2124">
            {steps.map((step, idx) => <EnhancedScrollReveal key={idx} delay={idx * 0.2} className="relative group" data-id="element-2125">
                {/* Timeline Marker */}
                <div className="hidden lg:flex absolute -top-10 left-1/2 -translate-x-1/2 flex-col items-center" data-id="element-2126">
                  <div className="px-3 py-1 bg-white border border-gray-200 rounded-full text-[10px] font-bold uppercase tracking-wider text-gray-500 shadow-sm mb-2" data-id="element-2127">
                    {step.time}
                  </div>
                  <div className="w-3 h-3 rounded-full bg-white border-2 border-gray-300 group-hover:border-primary-500 transition-colors" data-id="element-2128" />
                </div>

                {/* Card */}
                <div className="bg-white rounded-3xl p-8 shadow-soft-lg border border-gray-100 h-full hover:shadow-soft-xl hover:-translate-y-2 transition-all duration-300 relative overflow-hidden" data-id="element-2129">
                  {/* Step Number Background */}
                  <div className="absolute -right-4 -top-4 text-[120px] font-serif font-bold text-gray-50 opacity-50 leading-none select-none pointer-events-none" data-id="element-2130">
                    {step.id}
                  </div>

                  <div className={`w-16 h-16 rounded-2xl ${step.bg} flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 relative z-10`} data-id="element-2131">
                    <step.icon className={`w-8 h-8 ${step.color}`} strokeWidth={1.5} data-id="element-2132" />
                  </div>

                  <div className="relative z-10" data-id="element-2133">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-2134">
                      {step.subtitle}
                    </div>
                    <h3 className="text-2xl font-bold text-charcoal-900 font-serif mb-4" data-id="element-2135">
                      {step.title}
                    </h3>
                    <p className="text-charcoal-600 leading-relaxed mb-6 text-sm" data-id="element-2136">
                      {step.description}
                    </p>

                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${step.border} ${step.bg} bg-opacity-50`} data-id="element-2137">
                      <ShieldCheck className={`w-4 h-4 ${step.color}`} data-id="element-2138" />
                      <span className={`text-xs font-bold ${step.color}`} data-id="element-2139">
                        {step.detail}
                      </span>
                    </div>
                  </div>
                </div>
              </EnhancedScrollReveal>)}
          </div>
        </div>

        {/* CTA Section */}
        <EnhancedScrollReveal delay={0.4} className="text-center" data-id="element-2140">
          <div className="inline-flex flex-col items-center gap-4" data-id="element-2141">
            <p className="text-charcoal-500 font-medium" data-id="element-2142">
              ¿Listo para ver el cambio en tu mascota?
            </p>
            <Button size="xl" variant="gradient" className="rounded-full px-10 shadow-xl shadow-primary-900/20 hover:shadow-2xl hover:shadow-primary-900/30 transition-all" data-id="element-2143">
              Crear Plan Personalizado <ArrowRight className="ml-2 w-5 h-5" data-id="element-2144" />
            </Button>
            <div className="flex items-center gap-4 text-xs text-gray-400 mt-2" data-id="element-2145">
              <span className="flex items-center gap-1" data-id="element-2146">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" data-id="element-2147" /> Sin
                compromiso
              </span>
              <span className="flex items-center gap-1" data-id="element-2148">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" data-id="element-2149" />{' '}
                Cancelación flexible
              </span>
            </div>
          </div>
        </EnhancedScrollReveal>
      </div>
    </section>;
}